import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import LoginAuthUser from '../baseUrl/LoginAuthUser';
import LoginAuthSessionApi from '../baseUrl/LoginAuthSessionApi';
import { toast } from 'react-toastify';

// Define types
interface User {
  id: string;
  username: string;
  email?: string;
  role: string;
  apiKey?: string | null; // Masked API key (e.g., cgr_d54f...3ff5)
  hasApiKey?: boolean; // Boolean to check if user has an API key
  apiKeyCreatedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (username: string, password: string) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  checkSession: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUserProfile: (userData: Partial<User>) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<{ success: boolean; message?: string }>;
}

// Create Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  // Check session on mount and refresh
  const checkSession = useCallback(async () => {
    const token = localStorage.getItem('token');
    
    if (!token) {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false);
      return;
    }

    try {
      const response = await LoginAuthSessionApi.checkSession();
      
      if (response.success && response.data) {
        // User data includes masked apiKey and hasApiKey from session middleware
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        // Store new token if provided
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          LoginAuthUser.setAuthToken(response.data.token);
        }
      } else {
        // Invalid session
        localStorage.removeItem('token');
        LoginAuthUser.setAuthToken(null);
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      console.error('Session check failed:', error);
      localStorage.removeItem('token');
      LoginAuthUser.setAuthToken(null);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial session check
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Set up session refresh interval (every 10 minutes)
  useEffect(() => {
    if (isAuthenticated) {
      const refreshInterval = setInterval(() => {
        refreshSession();
      }, 10 * 60 * 1000); // 10 minutes

      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated]);

  // Login function
  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      const response = await LoginAuthUser.login(username, password);
      
      if (response.success && response.data) {
        // Store token
        const token = response.data.token;
        localStorage.setItem('token', token);
        LoginAuthUser.setAuthToken(token);
        
        // Store session ID if provided
        if (response.data.sessionId) {
          localStorage.setItem('sessionId', response.data.sessionId);
        }
        
        // Set user data (includes masked apiKey and hasApiKey)
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        return { success: true };
      } else {
        return { 
          success: false, 
          message: response.message || 'Login failed' 
        };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Network error occurred' 
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      // Call logout API
      await LoginAuthUser.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      // Clear local state regardless of API success
      localStorage.removeItem('token');
      localStorage.removeItem('sessionId');
      LoginAuthUser.setAuthToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Refresh session
  const refreshSession = async () => {
    try {
      const response = await LoginAuthSessionApi.refreshSession();
      
      if (response.success && response.data?.token) {
        localStorage.setItem('token', response.data.token);
        LoginAuthUser.setAuthToken(response.data.token);
        
        // Update user data if provided (includes masked apiKey)
        if (response.data.user) {
          setUser(response.data.user);
        }
      } else if (response.success && response.data) {
        // Token might not change but user data might be returned
        if (response.data.user) {
          setUser(response.data.user);
        }
      } else {
        // Session expired
        await logout();
        toast.warning('Your session has expired. Please login again.');
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      // Don't logout on refresh failure, let user continue
    }
  };

  // Refresh user data from server
  const refreshUser = async () => {
    try {
      const response = await LoginAuthUser.getUserProfile();
      
      if (response.success && response.data?.user) {
        // This includes the masked apiKey and hasApiKey
        setUser(response.data.user);
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  // Update user profile
  const updateUserProfile = async (userData: Partial<User>) => {
    try {
      const response = await LoginAuthUser.updateUserProfile(userData);
      
      if (response.success && response.data) {
        // Update the user state with the new data from server
        setUser(prevUser => ({
          ...prevUser!,
          ...response.data
        }));
        toast.success('Profile updated successfully');
      } else {
        toast.error(response.message || 'Failed to update profile');
        throw new Error(response.message || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Update profile error:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
      throw error;
    }
  };

  // Change password
  const changePassword = async (oldPassword: string, newPassword: string) => {
    try {
      const response = await LoginAuthUser.changePassword(oldPassword, newPassword);
      
      if (response.success) {
        return { success: true };
      } else {
        return { 
          success: false, 
          message: response.message || 'Failed to change password' 
        };
      }
    } catch (error: any) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Failed to change password' 
      };
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    refreshSession,
    checkSession,
    refreshUser,
    updateUserProfile,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
